import { sql } from "drizzle-orm";
import { boolean, index, integer, jsonb, pgTable, text, timestamp } from "drizzle-orm/pg-core";

import { boxes, endUsers, environments } from "./app.js";
import { tenant } from "./auth.js";

// ---- live pulls --------------------------------------------------------------------------------
// A store's live pulls feed (boxed's recent drops; docs/storefront.md, "Live pulls"): every open
// worth more than what it cost, written once its opener has had time to show it, so the feed
// never tells another shopper about a pull before its owner sees it. Every row is on its box's
// feed; one worth at least `FIRE_PULL_MIN_VALUE_CENTS` is also a fire pull, on the store's feed.

/** The item a live pull shows: copied from the open's drop-table row when it was rolled. */
export interface LivePullItem {
  name: string;
  imageUrl: string;
  /** Integer US cents. */
  value: number;
}

/**
 * One pull on a store's feeds. Written by Loot OS itself after an open commits
 * (`recordLivePulls`), never by a tenant; deleted after 30 days (`pruneLivePulls`). The rows are
 * public by design: what they carry is what `GET /v1/live-pulls` shows any visitor.
 */
export const livePulls = pgTable(
  "live_pulls",
  {
    /** The open's id (`opens.id`): one row per pull, however often it is recorded. */
    id: text("id").primaryKey(),
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    endUserId: text("end_user_id")
      .notNull()
      .references(() => endUsers.id, { onDelete: "cascade" }),
    boxId: text("box_id")
      .notNull()
      .references(() => boxes.id, { onDelete: "cascade" }),
    item: jsonb("item").$type<LivePullItem>().notNull(),
    /** What the open cost, in cents: the bar it cleared, and what its rarity is rated against. */
    costCents: integer("cost_cents").notNull(),
    /** The box's categories when it was pulled (`readBoxCategories`), for the category filter. */
    categories: text("categories").array().notNull().default([]),
    /** Worth at least `FIRE_PULL_MIN_VALUE_CENTS`: on the store's fire feed, not only its box's. */
    isFire: boolean("is_fire").notNull(),
    /** When the feeds may show it: the open's commit plus its opener's delay. */
    visibleAt: timestamp("visible_at", { withTimezone: true }).notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index("live_pulls_env_box_visible_idx").on(t.environmentId, t.boxId, t.visibleAt),
    index("live_pulls_env_fire_visible_idx")
      .on(t.environmentId, t.visibleAt)
      .where(sql`${t.isFire}`),
    // The daily prune deletes by age across every environment.
    index("live_pulls_visible_idx").on(t.visibleAt),
  ],
);
